import { useEffect, useRef, useState } from 'react';

import {
  type PatchReportingPeriodRequest,
  type PeriodUnresolvedUserDto,
  REPORTING_PERIOD_REASON_MAX,
  type ReportingPeriodDto,
} from '@time-tracking/shared';

import {
  useCreateReportingPeriod,
  useDeleteReportingPeriod,
  useReportingPeriods,
  useUnresolvedForPeriod,
  useUpdateReportingPeriodStatus,
} from '../../../api/admin.js';
import { useFocusTrap } from '../../../hooks/useFocusTrap.js';
import { ToastRegion } from '../../../components/ToastRegion.js';
import { useToasts } from '../../../components/useToasts.js';
import '../reporting-periods.css';
import { SubmissionDeadlineCard } from './SubmissionDeadlineCard.js';

// ---- Icon helpers -----------------------------------------------------------

function IconPlus() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function IconLock() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function IconUnlock() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function IconCal({ width = 14, height = 14 }: { width?: number; height?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={width}
      height={height}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function IconInfo() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function IconAlert({ width = 14, height = 14 }: { width?: number; height?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={width}
      height={height}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// ---- Date helpers ------------------------------------------------------------

function parseIso(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1);
}

function fmtDay(iso: string): string {
  return parseIso(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtRange(start: string, end: string): string {
  const sd = parseIso(start);
  const ed = parseIso(end);
  const sameYear = sd.getFullYear() === ed.getFullYear();
  const left = sameYear ? fmtDay(start) : `${fmtDay(start)}, ${sd.getFullYear()}`;
  return `${left} – ${fmtDay(end)}, ${ed.getFullYear()}`;
}

function isCurrent(p: ReportingPeriodDto): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const t = today.getTime();
  return parseIso(p.startDate).getTime() <= t && t <= parseIso(p.endDate).getTime();
}

// ---- Status badge -----------------------------------------------------------

function StatusBadge({ status }: { status: 'open' | 'closed' }) {
  return (
    <span className={`rp-badge ${status}`} aria-label={`Status: ${status}`}>
      <span className="dot" />
      {status === 'closed' ? 'Closed' : 'Open'}
    </span>
  );
}

// ---- Reason field -----------------------------------------------------------

interface ReasonFieldProps {
  id: string;
  label: string;
  hint: string;
  required: boolean;
  value: string;
  onChange: (value: string) => void;
}

/**
 * The reason recorded in the audit log against a close or reopen. Required-ness is
 * decided by `PatchReportingPeriodRequest` on the server; this only mirrors it so the
 * dialog can say so before the request is sent.
 */
function ReasonField({ id, label, hint, required, value, onChange }: ReasonFieldProps) {
  const hintId = `${id}-hint`;
  return (
    <div className="rp-reason">
      <label htmlFor={id}>{label}</label>
      <textarea
        id={id}
        rows={3}
        value={value}
        required={required}
        aria-required={required}
        aria-describedby={hintId}
        maxLength={REPORTING_PERIOD_REASON_MAX}
        onChange={(e) => onChange(e.target.value)}
      />
      <p id={hintId} className="rp-reason-hint">
        {hint}
      </p>
    </div>
  );
}

// ---- Add period form --------------------------------------------------------

interface AddPeriodFormProps {
  onCancel: () => void;
  onCreated: (name: string) => void;
}

function AddPeriodForm({ onCancel, onCreated }: AddPeriodFormProps) {
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const createMutation = useCreateReportingPeriod();

  const dateError = startDate && endDate && endDate < startDate;
  const formValid = name.trim().length > 0 && startDate !== '' && endDate !== '' && !dateError;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formValid) return;
    setCreateError(null);
    createMutation.mutate(
      { name: name.trim(), startDate, endDate },
      {
        onSuccess: () => {
          onCreated(name.trim());
        },
        onError: () => {
          setCreateError('Failed to create period. Please try again.');
        },
      },
    );
  }

  return (
    <form className="rp-form" onSubmit={handleSubmit} aria-label="Add reporting period">
      <div className="rp-form-row">
        <div className="rp-form-field grow">
          <label htmlFor="rp-name">Period name</label>
          <input
            id="rp-name"
            ref={nameInputRef}
            type="text"
            placeholder="e.g. FY27 Q1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            autoComplete="off"
            maxLength={100}
          />
        </div>
        <div className="rp-form-field">
          <label htmlFor="rp-start">Start date</label>
          <input
            id="rp-start"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="rp-form-field">
          <label htmlFor="rp-end">End date</label>
          <input
            id="rp-end"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="rp-form-actions">
          <button type="button" className="btn btn--secondary btn--sm" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn--primary btn--sm"
            disabled={!formValid || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating…' : 'Create period'}
          </button>
        </div>
      </div>
      {dateError ? (
        <div className="rp-form-err" role="alert">
          End date must be on or after the start date.
        </div>
      ) : null}
      {createError ? (
        <div className="rp-form-err" role="alert">
          {createError}
        </div>
      ) : null}
    </form>
  );
}

// ---- Main pane --------------------------------------------------------------

export function ReportingPeriodsPane() {
  const { data, isLoading } = useReportingPeriods();
  const toggleStatus = useUpdateReportingPeriodStatus();
  const deletePeriod = useDeleteReportingPeriod();

  const [adding, setAdding] = useState(false);
  const { toasts, pushToast, dismiss: dismissToast } = useToasts();
  const [confirmClose, setConfirmClose] = useState<ReportingPeriodDto | null>(null);
  const [closeReason, setCloseReason] = useState('');
  const [confirmReopen, setConfirmReopen] = useState<ReportingPeriodDto | null>(null);
  const [reopenReason, setReopenReason] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<ReportingPeriodDto | null>(null);
  const [deleteText, setDeleteText] = useState('');

  const closeDialogRef = useRef<HTMLDivElement>(null);
  const reopenDialogRef = useRef<HTMLDivElement>(null);
  const deleteDialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(closeDialogRef, confirmClose !== null);
  useFocusTrap(reopenDialogRef, confirmReopen !== null);
  useFocusTrap(deleteDialogRef, confirmDelete !== null);

  const { data: unresolvedData, isLoading: unresolvedLoading } = useUnresolvedForPeriod(
    confirmClose?.periodId ?? null,
  );

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setConfirmClose(null);
        setConfirmReopen(null);
        setConfirmDelete(null);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const periods = data?.periods ?? [];
  const sorted = [...periods].sort((a, b) => b.startDate.localeCompare(a.startDate));
  const closedCount = periods.filter((p) => p.status === 'closed').length;

  function submitStatus(p: ReportingPeriodDto, request: PatchReportingPeriodRequest) {
    toggleStatus.mutate(
      { periodId: p.periodId, ...request },
      {
        onSuccess: () => {
          if (request.status === 'closed') {
            pushToast(`Closed "${p.name}". Its weeks are now locked for everyone.`, 'warn', 5000);
          } else {
            pushToast(`Reopened "${p.name}".`, 'success');
          }
        },
        onError: () => pushToast('Failed to update status. Please try again.', 'error'),
      },
    );
  }

  // Closing a period with nothing unresolved is the routine end of a month and stays
  // one click; a reason is optional on close, so there is nothing to ask for. Closing
  // over unresolved timesheets goes through the confirm dialog, which offers the
  // optional reason. Reopening always asks, because the reason is mandatory.
  function requestToggle(p: ReportingPeriodDto) {
    if (p.status === 'closed') {
      setConfirmReopen(p);
      setReopenReason('');
      return;
    }
    if (p.unresolvedCount > 0) {
      setConfirmClose(p);
      setCloseReason('');
      return;
    }
    submitStatus(p, { status: 'closed' });
  }

  function confirmCloseDo() {
    if (!confirmClose) return;
    const reason = closeReason.trim();
    submitStatus(confirmClose, { status: 'closed', ...(reason ? { reason } : {}) });
    setConfirmClose(null);
  }

  const reopenReady = reopenReason.trim().length > 0;

  function confirmReopenDo() {
    if (!confirmReopen || !reopenReady) return;
    submitStatus(confirmReopen, { status: 'open', reason: reopenReason.trim() });
    setConfirmReopen(null);
  }

  function requestDelete(p: ReportingPeriodDto) {
    setConfirmDelete(p);
    setDeleteText('');
  }

  function performDelete() {
    if (!confirmDelete) return;
    const p = confirmDelete;
    setConfirmDelete(null);
    setDeleteText('');
    deletePeriod.mutate(p.periodId, {
      onSuccess: () => pushToast(`Deleted "${p.name}".`, 'success'),
      onError: () => pushToast('Failed to delete period. Please try again.', 'error'),
    });
  }

  function handleCreated(name: string) {
    setAdding(false);
    pushToast(`Created "${name}".`, 'success');
  }

  const deleteReady =
    !confirmDelete ||
    confirmDelete.status !== 'closed' ||
    deleteText.trim().toLowerCase() === confirmDelete.name.trim().toLowerCase();

  return (
    <div className="rp">
      {/* The deadline lives here because this pane already owns the calendar rules that
          govern a week's lifecycle: periods decide when a week locks, the deadline
          decides when it counts as late. Extracted rather than inlined — this file is
          long enough, and the control earns its own tests. */}
      <SubmissionDeadlineCard />

      <div className="rp-head">
        <div>
          <h2>Reporting periods</h2>
          <p>
            Group weeks into named periods (typically fiscal quarters). <strong>Closing</strong> a
            period locks every week inside its dates for everyone — contributors can&rsquo;t submit,
            edit or recall, and approvers can&rsquo;t approve, return or unapprove.
          </p>
        </div>
        <div className="rp-head-right">
          {!adding && (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => setAdding(true)}
              aria-label="Add reporting period"
            >
              <IconPlus /> Add period
            </button>
          )}
        </div>
      </div>

      {adding && <AddPeriodForm onCancel={() => setAdding(false)} onCreated={handleCreated} />}

      {isLoading ? (
        <p style={{ color: 'var(--fg3)', fontSize: 'var(--fs-14)' }}>Loading…</p>
      ) : periods.length === 0 ? (
        <div className="rp-empty">
          <IconCal width={26} height={26} />
          <h3>No reporting periods yet</h3>
          <p>Add your first period — until you close one, all weeks stay open for submission.</p>
          {!adding && (
            <button type="button" className="btn btn--primary" onClick={() => setAdding(true)}>
              <IconPlus /> Add period
            </button>
          )}
        </div>
      ) : (
        <div className="rp-table" role="table" aria-label="Reporting periods">
          <div className="rp-tr head" role="row">
            <div className="rp-td" role="columnheader">
              Period
            </div>
            <div className="rp-td" role="columnheader">
              Dates
            </div>
            <div className="rp-td" role="columnheader">
              Status
            </div>
            <div className="rp-td right" role="columnheader">
              Actions
            </div>
          </div>
          {sorted.map((p) => (
            <div key={p.periodId} className="rp-tr" role="row">
              <div className="rp-td name" role="cell">
                {p.name}
                {isCurrent(p) && (
                  <span className="rp-current-tag" aria-label="Current period">
                    current
                  </span>
                )}
              </div>
              <div className="rp-td muted" role="cell">
                {fmtRange(p.startDate, p.endDate)}
              </div>
              <div className="rp-td" role="cell">
                <StatusBadge status={p.status} />
                {p.status === 'open' && p.unresolvedCount > 0 && (
                  <div className="rp-unresolved">
                    <IconAlert />
                    {p.unresolvedCount} unresolved
                  </div>
                )}
              </div>
              <div className="rp-td right" role="cell">
                <div className="rp-row-actions">
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={() => requestToggle(p)}
                    disabled={toggleStatus.isPending}
                    aria-label={p.status === 'open' ? `Close ${p.name}` : `Reopen ${p.name}`}
                  >
                    {p.status === 'open' ? (
                      <>
                        <IconLock /> Close
                      </>
                    ) : (
                      <>
                        <IconUnlock /> Reopen
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm rp-danger-text"
                    disabled={deletePeriod.isPending}
                    title={`Delete ${p.name}`}
                    aria-label={`Delete ${p.name}`}
                    onClick={() => requestDelete(p)}
                  >
                    <IconTrash />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rp-foot">
        <IconInfo />
        <span>
          {closedCount} closed &middot; {periods.length - closedCount} open. Deleting a period
          can&rsquo;t be undone — closed periods ask you to retype the name first, since their weeks
          are already locked.
        </span>
      </div>

      <ToastRegion toasts={toasts} onDismiss={dismissToast} />

      {confirmClose !== null && (
        <div className="dialog-backdrop" onClick={() => setConfirmClose(null)}>
          <div
            ref={closeDialogRef}
            className="dialog rp-confirm"
            role="alertdialog"
            aria-modal="true"
            aria-label={`Close ${confirmClose.name}?`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rp-confirm-body">
              <div className="rp-confirm-ic">
                <IconAlert width={20} height={20} />
              </div>
              <div className="rp-confirm-text">
                <h3>Close &ldquo;{confirmClose.name}&rdquo;?</h3>
                <p>
                  <strong>
                    {confirmClose.unresolvedCount}{' '}
                    {confirmClose.unresolvedCount === 1 ? 'person has' : 'people have'} unresolved
                    timesheets
                  </strong>{' '}
                  in this period&rsquo;s date range. Closing it locks every week inside for everyone
                  — no one can submit, recall, approve or return these until you reopen the period.
                </p>
                {unresolvedLoading ? (
                  <div className="rp-userlist">
                    <p style={{ fontSize: 'var(--fs-12)', color: 'var(--fg3)', padding: '8px 0' }}>
                      Loading…
                    </p>
                  </div>
                ) : unresolvedData && unresolvedData.users.length > 0 ? (
                  <div className="rp-userlist">
                    <div className="rp-userlist-head">
                      <span>
                        <strong>{unresolvedData.pending}</strong> awaiting approval
                      </span>
                      <span className="sep" aria-hidden="true">
                        &middot;
                      </span>
                      <span>
                        <strong>{unresolvedData.missing}</strong> not yet approved
                      </span>
                    </div>
                    <ul>
                      {unresolvedData.users.map((usr: PeriodUnresolvedUserDto) => (
                        <li key={usr.userId}>
                          <span className="rp-ava" aria-hidden="true">
                            {usr.initials}
                          </span>
                          <span className="rp-who">
                            <span className="nm">{usr.displayName}</span>
                            <span className="sub">
                              {usr.utEid ?? '—'}
                              {usr.teamName ? ` · ${usr.teamName}` : ''}
                            </span>
                          </span>
                          <span className="rp-ucounts">
                            <span className="rp-ucount-total">
                              {usr.weekCount} week{usr.weekCount === 1 ? '' : 's'}
                            </span>
                            <span className="rp-ucount-split">
                              {usr.pendingCount > 0 && (
                                <span className="seg pending">{usr.pendingCount} awaiting</span>
                              )}
                              {usr.pendingCount > 0 && usr.missingCount > 0 && (
                                <span className="seg-sep" aria-hidden="true">
                                  &middot;
                                </span>
                              )}
                              {usr.missingCount > 0 && (
                                <span className="seg missing">{usr.missingCount} not approved</span>
                              )}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <ReasonField
                  id="rp-close-reason"
                  label="Reason (optional)"
                  hint="Saved to the audit log with your name and the time."
                  required={false}
                  value={closeReason}
                  onChange={setCloseReason}
                />
              </div>
            </div>
            <div className="rp-confirm-footer">
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => setConfirmClose(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--destructive btn--sm"
                onClick={confirmCloseDo}
              >
                <IconLock /> Close anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmReopen !== null && (
        <div className="dialog-backdrop" onClick={() => setConfirmReopen(null)}>
          <div
            ref={reopenDialogRef}
            className="dialog rp-confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="rp-reopen-title"
            aria-describedby="rp-reopen-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                confirmReopenDo();
              }}
            >
              <div className="rp-confirm-body">
                <div className="rp-confirm-ic">
                  <IconUnlock />
                </div>
                <div className="rp-confirm-text">
                  <h3 id="rp-reopen-title">Reopen &ldquo;{confirmReopen.name}&rdquo;?</h3>
                  <p id="rp-reopen-desc">
                    Reopening unlocks submit, editing and recall for every week from{' '}
                    <strong>{fmtRange(confirmReopen.startDate, confirmReopen.endDate)}</strong>, so
                    hours that have already been reported can change.
                  </p>
                  <ReasonField
                    id="rp-reopen-reason"
                    label="Reason for reopening (required)"
                    hint="Required to reopen. Saved to the audit log with your name and the time."
                    required
                    value={reopenReason}
                    onChange={setReopenReason}
                  />
                </div>
              </div>
              <div className="rp-confirm-footer">
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => setConfirmReopen(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn--primary btn--sm"
                  disabled={!reopenReady || toggleStatus.isPending}
                >
                  <IconUnlock /> Reopen period
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDelete !== null && (
        <div className="dialog-backdrop" onClick={() => setConfirmDelete(null)}>
          <div
            ref={deleteDialogRef}
            className="dialog rp-confirm rp-delete"
            role="alertdialog"
            aria-modal="true"
            aria-label={`Delete ${confirmDelete.name}?`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rp-confirm-body">
              <div className="rp-confirm-ic rp-confirm-ic--danger">
                <IconTrash />
              </div>
              <div className="rp-confirm-text">
                <h3>Delete &ldquo;{confirmDelete.name}&rdquo;?</h3>
                {confirmDelete.status === 'closed' ? (
                  <p>
                    This is a <strong>closed</strong> period — its weeks are locked and its
                    timesheets finalized. Deleting it <strong>permanently</strong> removes the
                    grouping and reopens those weeks for editing. This can&rsquo;t be undone.
                  </p>
                ) : (
                  <p>
                    Deleting removes this period grouping. Its weeks stay open and ungrouped — no
                    hours are lost — but you&rsquo;ll need to recreate the period to group them
                    again. This can&rsquo;t be undone.
                  </p>
                )}
                {confirmDelete.status !== 'closed' && confirmDelete.unresolvedCount > 0 && (
                  <div className="rp-delete-note">
                    <IconAlert />
                    <span>
                      {confirmDelete.unresolvedCount}{' '}
                      {confirmDelete.unresolvedCount === 1 ? 'person has' : 'people have'}{' '}
                      unresolved timesheets inside this period.
                    </span>
                  </div>
                )}
                {confirmDelete.status === 'closed' && (
                  <div className="rp-delete-confirm">
                    <label htmlFor="rp-delete-text">
                      Type <strong>{confirmDelete.name}</strong> to confirm
                    </label>
                    <input
                      id="rp-delete-text"
                      autoFocus
                      type="text"
                      value={deleteText}
                      placeholder={confirmDelete.name}
                      onChange={(e) => setDeleteText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && deleteReady) performDelete();
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="rp-confirm-footer">
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--destructive btn--sm"
                disabled={!deleteReady || deletePeriod.isPending}
                onClick={performDelete}
              >
                <IconTrash /> Delete period
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
